import React, { useState } from "react";
import { Heart, MessageCircle, Share2, Shield, EyeOff, Flag } from "lucide-react";
import { api, fileUrl } from "@/lib/api";
import { timeAgo } from "@/lib/time";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import CalmBadge from "@/components/CalmBadge";
import ReportDialog from "@/components/ReportDialog";
import PostComments from "@/components/PostComments";

function PostHeader({ post, isMine, onReport }) {
  const isAnon = post.anonymous;
  return (
    <header className="flex items-center gap-3 mb-3">
      {isAnon ? (
        <div className="w-10 h-10 rounded-full border-2 border-foreground bg-secondary flex items-center justify-center">
          <EyeOff size={16} strokeWidth={2.5} />
        </div>
      ) : (
        <Link to={`/profile/${post.author.id}`}>
          <div className="w-10 h-10 rounded-full border-2 border-foreground overflow-hidden bg-accent flex items-center justify-center font-bold">
            {post.author.avatar_path ? <img src={fileUrl(post.author.avatar_path)} className="w-full h-full object-cover" alt="" /> : post.author.display_name?.[0]}
          </div>
        </Link>
      )}
      <div className="flex-1">
        <div className="font-semibold leading-tight flex items-center gap-2">
          <span>{isAnon ? "Anonymous" : post.author.display_name}</span>
          {!isAnon && post.author.is_premium && <CalmBadge size="xs" />}
        </div>
        <div className="text-xs text-muted-foreground">{isAnon ? "shared anonymously" : `@${post.author.username}`} · {timeAgo(post.created_at)}</div>
      </div>
      {!isMine && !isAnon && (
        <button
          onClick={onReport}
          aria-label="Report post"
          title="Report post"
          data-testid={`report-post-btn-${post.id}`}
          className="brutal-btn p-1.5 bg-card text-muted-foreground hover:text-foreground"
        >
          <Flag size={14} strokeWidth={2.5} />
        </button>
      )}
    </header>
  );
}

export default function PostCard({ post, hideLikeCounts }) {
  const { user } = useAuth();
  const [liked, setLiked] = useState(post.liked_by_me);
  const [likes, setLikes] = useState(post.likes_count);
  const [showComments, setShowComments] = useState(false);
  const [reportOpen, setReportOpen] = useState(false);
  const isMine = user && post.author?.id === user.id;
  const mod = post.moderation || {};

  const toggleLike = async () => {
    try {
      const r = await api.post(`/posts/${post.id}/like`);
      setLiked(r.data.liked);
      setLikes(r.data.likes_count);
      if (r.data.liked) window.dispatchEvent(new CustomEvent("haeven:mission"));
    } catch { toast.error("Could not react"); }
  };

  const sharePost = () => {
    navigator.clipboard?.writeText(`${window.location.origin}/feed#${post.id}`);
    toast.success("Link copied");
  };

  return (
    <article className="brutal-card p-5 md:p-6 bg-card animate-fade-up" data-testid={`post-${post.id}`}>
      <PostHeader post={post} isMine={isMine} onReport={() => setReportOpen(true)} />
      <ReportDialog
        open={reportOpen}
        onClose={() => setReportOpen(false)}
        targetType="post"
        targetId={post.id}
        targetLabel={`Post by @${post.author?.username || "unknown"}`}
      />

      {mod.flag && !mod.pending && (
        <div className="mb-3 brutal-card bg-accent p-3 flex items-start gap-2" data-testid="mod-flag">
          <Shield size={16} strokeWidth={2.5} className="mt-1" />
          <div className="text-sm"><b>Gentle note:</b> {mod.reason || "This post was flagged by our care AI."}{mod.suggestion ? ` Suggested rewording: "${mod.suggestion}"` : ""}</div>
        </div>
      )}

      <p className="whitespace-pre-wrap leading-relaxed">{post.content}</p>

      {post.image_path && (
        <div className="mt-3 brutal-card overflow-hidden p-0">
          <img src={fileUrl(post.image_path)} alt="" className="w-full max-h-[500px] object-cover" />
        </div>
      )}

      <footer className="mt-4 flex items-center gap-2">
        <button data-testid={`like-${post.id}`} onClick={toggleLike}
          className={`brutal-btn px-3 py-2 flex items-center gap-2 ${liked ? "bg-primary text-primary-foreground" : "bg-card"}`}>
          <Heart size={16} strokeWidth={2.5} fill={liked ? "currentColor" : "none"} />
          {!hideLikeCounts && <span className="text-sm">{likes}</span>}
        </button>
        <button data-testid={`comment-${post.id}`} onClick={() => setShowComments(!showComments)} className="brutal-btn px-3 py-2 bg-card flex items-center gap-2">
          <MessageCircle size={16} strokeWidth={2.5} /> <span className="text-sm">{post.comments_count}</span>
        </button>
        <button className="brutal-btn px-3 py-2 bg-card flex items-center gap-2" data-testid={`share-${post.id}`} onClick={sharePost}>
          <Share2 size={16} strokeWidth={2.5} />
        </button>
      </footer>

      {showComments && <PostComments postId={post.id} />}
    </article>
  );
}